import React, { useCallback, useContext } from 'react';
import { Text, Pressable, View } from 'react-native';
import PropTypes from 'prop-types';
import { t } from 'Locale';
import { useStore } from 'Store';
import { isContentBlocked, isContentLocked } from 'Utils/parentalControlUtils';
import { ConfirmationModalContext } from '../ConfirmationModal';
import Checkbox from '../Checkbox';
import styles from './RecordingItem.jss';
import Icon from '../Icon';
import { CachedImage } from '../CachedImage';

const RecordingItem = ({
    item,
    iconUrl,
    onPress,
    title,
    subTitle,
    isEditing,
    isSelected,
    isFirst,
    onCheckbox,
    IPTVonly,
    parentalRating,
    adult,
    index
}) => {
    const { parentalLockConfig } = useStore();
    const { setProtectedContent } = useContext(ConfirmationModalContext);

    const adultLock = isContentLocked(adult, parentalLockConfig);
    const ageBlock = isContentBlocked(parentalRating, parentalLockConfig);

    const protectedPress = useCallback(
        (event) => {
            if (ageBlock || adultLock) {
                setProtectedContent(item);
            } else {
                onPress({ data: {}, event });
            }
        },
        [adultLock, ageBlock]
    );

    return (
        <View style={styles.row} testID={`recording_${index}`}>
            <Pressable style={styles.container} onPress={protectedPress}>
                {ageBlock || adultLock ? (
                    <Icon
                        name="lock"
                        style={[styles.lockIcon, isEditing && styles.hiddenIconStyle]}
                    />
                ) : (
                    <CachedImage
                        source={{ uri: iconUrl }}
                        style={[styles.icon, isEditing && styles.hiddenIconStyle]}
                        resizeMode="contain"
                    />
                )}
                <View style={[styles.textArea, isFirst && styles.firstTextArea]}>
                    <View style={styles.metadata}>
                        <Text numberOfLines={1} style={styles.programTitle}>
                            {adultLock ? t('RECORDINGS.LOCKED') : title}
                        </Text>
                        <Text
                            style={[
                                styles.programSubtitle,
                                IPTVonly && styles.programSubtitleNoMargin
                            ]}
                        >
                            {subTitle}
                        </Text>
                        {IPTVonly && (
                            <View style={styles.iptvOnly}>
                                <Icon name="info" style={styles.iptvInfoIcon} />
                                <Text style={styles.iptvOnlyText}>
                                    {t('RECORDINGS.IPTV_RECORDINGS')}
                                </Text>
                            </View>
                        )}
                    </View>
                    {isEditing && (
                        <Checkbox
                            key={isSelected ? 'selected' : 'empty'}
                            style={styles.checkbox}
                            checked={isSelected}
                            onPress={onCheckbox}
                            hitSlop={{ top: 8, left: 8, right: 8, bottom: 8 }}
                            testID={`select_${index}`}
                        />
                    )}
                </View>
            </Pressable>
        </View>
    );
};

RecordingItem.defaultProps = {
    iconUrl: '',
    title: '',
    subTitle: '',
    isEditing: false,
    isSelected: false,
    IPTVonly: false,
    isFirst: false,
    parentalRating: null,
    adult: false,
    onPress: () => {},
    onCheckbox: () => {},
    index: undefined
};

RecordingItem.propTypes = {
    item: PropTypes.shape({}).isRequired,
    iconUrl: PropTypes.string,
    title: PropTypes.string,
    subTitle: PropTypes.string,
    isEditing: PropTypes.bool,
    isSelected: PropTypes.bool,
    IPTVonly: PropTypes.bool,
    isFirst: PropTypes.bool,
    parentalRating: PropTypes.string,
    adult: PropTypes.bool,
    onPress: PropTypes.func,
    onCheckbox: PropTypes.func,
    index: PropTypes.number
};

export default RecordingItem;
